import { Box, Input, Link, Text, Textarea, VStack } from "@chakra-ui/react";
import {
  EyebrowBadge,
  SectionCard,
  ToggleChoice,
  FormField,
  ChecklistCard,
  DocumentCard,
} from "../components";
import { ONBOARDING_LINKS, type DocumentKey } from "../constants";
import type { FormData } from "../types";

interface Step6Props {
  data: FormData;
  updateData: (field: string, value: string | boolean) => void;
  fieldErrors: Record<string, string>;
  uploadedDocs: Partial<Record<DocumentKey, string>>;
  handleFileSelect: (key: DocumentKey) => void;
  handleFileChange: (key: DocumentKey, e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRefs: React.MutableRefObject<Partial<Record<string, HTMLInputElement | null>>>;
}

export function Step6Compliance({
  data,
  updateData,
  fieldErrors,
  uploadedDocs,
  handleFileSelect,
  handleFileChange,
  fileInputRefs,
}: Step6Props) {
  return (
    <Box spaceY={8}>
      <Box spaceY={3}>
        <EyebrowBadge stepId={6} />
        <Box spaceY={2}>
          <Text fontSize="3xl" fontWeight="bold" color="gray.900" letterSpacing="tight">
            Compliance y soportes
          </Text>
          <Text fontSize="lg" color="gray.500" maxW="2xl">
            Cerremos con validaciones de riesgo, declaraciones y carga de documentos.
          </Text>
        </Box>
      </Box>

      <SectionCard
        title="Cuestionario de riesgo (SARLAFT)"
        description="Responde estas preguntas para completar la validación de cumplimiento."
      >
        <Box spaceY={4}>
          <ToggleChoice
            value={data.pep}
            onChange={(val: string) => updateData("pep", val)}
            title="¿Alguno de los administradores o accionistas es una Persona Expuesta Políticamente (PEP)?"
          />
          {data.pep === "si" && (
            <Box bg="orange.50" borderRadius="xl" p={5}>
              <VStack align="start" gap={4}>
                <Text fontSize="sm" fontWeight="semibold" color="orange.700">
                  Cuéntanos un poco más sobre la persona PEP
                </Text>
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} w="full">
                  <FormField label="Nombre de la persona PEP" error={fieldErrors.pepNombre}>
                    <Input
                      value={data.pepNombre}
                      onChange={(e) => updateData("pepNombre", e.target.value)}
                      placeholder="Nombre completo"
                    />
                  </FormField>
                  <FormField label="Cargo o relación con la compañía" error={fieldErrors.pepCargo}>
                    <Input
                      value={data.pepCargo}
                      onChange={(e) => updateData("pepCargo", e.target.value)}
                      placeholder="Ej. representante legal, accionista"
                    />
                  </FormField>
                </Box>
                <Box w="full">
                  <FormField label="Detalle adicional" error={fieldErrors.pepDetalle}>
                    <Textarea
                      value={data.pepDetalle}
                      onChange={(e) => updateData("pepDetalle", e.target.value)}
                      placeholder="Cuéntanos cualquier detalle relevante"
                      minH="80px"
                      resize="none"
                    />
                  </FormField>
                </Box>
              </VStack>
            </Box>
          )}
          <ToggleChoice
            value={data.cripto}
            onChange={(val: string) => updateData("cripto", val)}
            title="¿La empresa es o se relaciona con proveedores de activos virtuales?"
          />
          {data.cripto === "si" && (
            <Box bg="orange.50" borderRadius="xl" p={5}>
              <VStack align="start" gap={4}>
                <Text fontSize="sm" fontWeight="semibold" color="orange.700">
                  Cuéntanos un poco más sobre esta relación
                </Text>
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} w="full">
                  <FormField label="Tipo de actividad con activos virtuales" error={fieldErrors.criptoActividad}>
                    <Input
                      value={data.criptoActividad}
                      onChange={(e) => updateData("criptoActividad", e.target.value)}
                      placeholder="Ej. pasarela, custodia, trading"
                    />
                  </FormField>
                  <FormField label="Relación con la operación" error={fieldErrors.criptoRelacion}>
                    <Input
                      value={data.criptoRelacion}
                      onChange={(e) => updateData("criptoRelacion", e.target.value)}
                      placeholder="Describe la relación con tu compañía"
                    />
                  </FormField>
                </Box>
                <Box w="full">
                  <FormField label="Detalle adicional" error={fieldErrors.criptoDetalle}>
                    <Textarea
                      value={data.criptoDetalle}
                      onChange={(e) => updateData("criptoDetalle", e.target.value)}
                      placeholder="Cuéntanos cualquier detalle relevante"
                      minH="80px"
                      resize="none"
                    />
                  </FormField>
                </Box>
              </VStack>
            </Box>
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Declaraciones y aceptación"
        description="Estas autorizaciones son necesarias para cerrar el proceso de alta."
      >
        <Box spaceY={4}>
          <ChecklistCard
            checked={data.origenFondos}
            onCheckedChange={(val: boolean) => updateData("origenFondos", val)}
            title="Declaración de origen de fondos"
            error={fieldErrors.origenFondos}
          >
            Declaro que mis recursos y los de la sociedad no provienen ni serán destinados a actividades ilícitas
            (LA/FT). Autorizo validaciones y cruces en bases de datos.
          </ChecklistCard>
          <ChecklistCard
            checked={data.tratamientoDatos}
            onCheckedChange={(val: boolean) => updateData("tratamientoDatos", val)}
            title="Autorización de datos personales"
            error={fieldErrors.tratamientoDatos}
          >
            Autorizo a Palomma S.A.S. para el tratamiento de datos y consultas en listas restrictivas según su{" "}
            <Link
              href={ONBOARDING_LINKS.privacy}
              target="_blank"
              rel="noreferrer"
              color="palomma.700"
              textDecoration="underline"
              textUnderlineOffset="4px"
            >
              política de privacidad
            </Link>
            .
          </ChecklistCard>
          <ChecklistCard
            checked={data.aceptaContrato}
            onCheckedChange={(val: boolean) => updateData("aceptaContrato", val)}
            title="Aceptación de reglamento y términos"
            error={fieldErrors.aceptaContrato}
          >
            He leído y acepto el{" "}
            <Link
              href={ONBOARDING_LINKS.rules}
              target="_blank"
              rel="noreferrer"
              color="palomma.700"
              textDecoration="underline"
              textUnderlineOffset="4px"
            >
              reglamento
            </Link>{" "}
            y los{" "}
            <Link
              href={ONBOARDING_LINKS.terms}
              target="_blank"
              rel="noreferrer"
              color="palomma.700"
              textDecoration="underline"
              textUnderlineOffset="4px"
            >
              términos y condiciones
            </Link>
            .
          </ChecklistCard>
        </Box>
      </SectionCard>

      <Box spaceY={4}>
        <Box spaceY={1}>
          <Text fontSize="2xl" fontWeight="bold" color="gray.900" letterSpacing="tight">
            Documentos adjuntos
          </Text>
          <Text fontSize="sm" color="gray.500">
            Adjunta los soportes requeridos para cerrar la revisión operativa.
          </Text>
        </Box>
        <Box spaceY={4}>
          <DocumentCard
            docKey="rut"
            docName="RUT actualizado"
            isUploaded={!!uploadedDocs.rut}
            error={fieldErrors.rut}
            onFileClick={handleFileSelect}
            onFileChange={handleFileChange}
            fileInputRefs={fileInputRefs}
          />
          <DocumentCard
            docKey="certificacion_bancaria"
            docName="Certificación bancaria"
            isUploaded={!!uploadedDocs.certificacion_bancaria}
            error={fieldErrors.certificacion_bancaria}
            onFileClick={handleFileSelect}
            onFileChange={handleFileChange}
            fileInputRefs={fileInputRefs}
          />
          <DocumentCard
            docKey="camara_comercio"
            docName="Cámara de comercio"
            isUploaded={!!uploadedDocs.camara_comercio}
            error={fieldErrors.camara_comercio}
            onFileClick={handleFileSelect}
            onFileChange={handleFileChange}
            fileInputRefs={fileInputRefs}
          />
        </Box>
      </Box>
    </Box>
  );
}
